import { ref, getBytes } from 'firebase/storage';

import { Storage } from '../../config/firebase';
import bundledCoursesData from './courses.json';
import { Course, Hole, CourseConfiguration } from '../data_types/courseTypes';

// Exported from the game (Tools → DGV → Export Game Data) and uploaded to Firebase Storage,
// so new courses show up without a website deploy. The bundled courses.json is only a fallback.
const COURSES_STORAGE_PATH = 'gamedata/courses/courses.json';


// lookup tables
export const shortNameToCourse: Record<string, Course> = {};
export const shortNameIdToHole: Record<string, Hole> = {};
export const sceneNameToHole: Record<string, Hole> = {};
export const shortNameIdToConfigName: Record<string, string> = {};

// TODO: Usage for these, somewhere?
export const fullCourseConfigIdToCourse: Record<number, Course> = {};
export const fullShortNameToFullCourseConfig: Record<string, CourseConfiguration> = {};

const fetchCoursesData = async (): Promise<Course[]> => {
    try {
        const bytes = await getBytes(ref(Storage, COURSES_STORAGE_PATH));
        return JSON.parse(new TextDecoder().decode(bytes)) as Course[];
    } catch (error) {
        console.warn(`Could not load ${COURSES_STORAGE_PATH} from Firebase Storage, using bundled courses`, error);
        return bundledCoursesData as Course[];
    }
};

const clearLookup = (lookup: Record<string, unknown>) => {
    Object.keys(lookup).forEach((key) => delete lookup[key]);
};

// load and normalize courses
export const loadCourses = async (): Promise<Course[]> => {
    const loadedCourses = await fetchCoursesData();
    const normalizedCourses = loadedCourses.map((course) => normalizeCourseConfigurations(course));

    // Populate lookup tables
    [shortNameToCourse, shortNameIdToHole, sceneNameToHole, shortNameIdToConfigName].forEach(clearLookup);
    normalizedCourses.forEach((course) => {
        shortNameToCourse[course.shortName] = course;

        course.configurations.forEach((config) => {
            config.holes.forEach((hole) => {
                shortNameIdToHole[hole.shortNameId] = hole;
                sceneNameToHole[hole.sceneName] = hole;

                shortNameIdToConfigName[hole.shortNameId] = config.name;
            });
        });
    });

    return normalizedCourses;
};

const extendCourseConfiguration = (holes: Hole[]): Hole[] => {
    if (holes.length >= 9) return holes.slice(0, 9);
    
    const extendedHoles = [...holes];
    const additionalHolesNeeded = 9 - holes.length;
    const reversedHoles = [...holes].reverse().slice(1);
    
    for (let i = 0; i < additionalHolesNeeded; i++) {
        extendedHoles.push(reversedHoles[i % reversedHoles.length]);
    }

    return extendedHoles;
};

export interface SplitCourseConfiguration extends CourseConfiguration {
    portion?: 'F9' | 'B9';
}

// Helper function to split configurations into F9 and B9
const splitCourseConfiguration = (config: CourseConfiguration): CourseConfiguration[] => {
    if (config.holes.length > 9) {
        const front9Holes = config.holes.slice(0, 9);
        const back9Holes = config.holes.slice(9, 18);

        const front9Config: SplitCourseConfiguration = {
            ...config,
            portion: 'F9',
            name: `${config.name} F9`,
            holes: front9Holes,
        };

        const back9Config: SplitCourseConfiguration = {
            ...config,
            portion: 'B9',
            name: `${config.name} B9`,
            holes: back9Holes,
        };

        return [front9Config, back9Config];
    }

    // If holes are 9 or fewer, return the original configuration
    return [config];
};

// Function to normalize course configurations
const normalizeCourseConfigurations = (course: Course): Course => {
    const normalizedConfigurations: CourseConfiguration[] = [];

    course.configurations.forEach((config) => {
        const holeCount = config.holes.length;

        if (holeCount < 9) {
            // Extend holes to 9
            const extendedHoles = extendCourseConfiguration(config.holes);
            normalizedConfigurations.push({
                ...config,
                holes: extendedHoles,
            });
        } else if (holeCount > 9 && holeCount <= 18) {
            // Split into F9 and B9
            const splitConfigs = splitCourseConfiguration(config);
            normalizedConfigurations.push(...splitConfigs);
        } else if (holeCount > 18) {
            console.warn(`Configuration ${config.name} in course ${course.name} has more than 18 holes. Only the first 18 will be processed.`);
            const limitedHoles = config.holes.slice(0, 18);
            const splitConfigs = splitCourseConfiguration({ ...config, holes: limitedHoles });
            normalizedConfigurations.push(...splitConfigs);
        } else {
            // Exactly 9 holes, no action needed
            normalizedConfigurations.push(config);
        }
    });

    return {
        ...course,
        configurations: normalizedConfigurations,
    };
};
